import { useState } from 'react';

import { ChakraBox, ChakraFlex, ChakraText, FAIconName, Icon } from '@equip.health/ui';

import MaiChatThinking from './MaiChatThinking';
import { MaiToolCall } from './maiChat.types';
import { toolCallLabel } from './maiChat.util';

interface MaiChatToolStepsProps {
  toolCalls: MaiToolCall[];
  isThinking?: boolean;
  onToggle: (isExpanded: boolean) => void;
}

const SPINNER_KEYFRAMES = `
  @keyframes mai-chat-step-spin {
    to { transform: rotate(360deg); }
  }
  @media (prefers-reduced-motion: no-preference) {
    .mai-chat-step-spinner {
      animation: mai-chat-step-spin 0.8s linear infinite;
    }
  }
`;

const StepIcon = ({ status }: { status: MaiToolCall['status'] }) => {
  if (status === 'success') {
    return (
      <Icon name={FAIconName.circleCheckRegular} color="success.primary" size="sm" />
    );
  }
  if (status === 'error') {
    return (
      <Icon
        name={FAIconName.exclamationCircleRegular}
        color="error.primary"
        size="sm"
      />
    );
  }
  return (
    <ChakraBox className="mai-chat-step-spinner" display="flex" lineHeight="0">
      <Icon name={FAIconName.spinnerRegular} color="neutral.tertiary" size="sm" />
    </ChakraBox>
  );
};

const StepRow = ({ toolCall }: { toolCall: MaiToolCall }) => (
  <ChakraFlex
    data-testid="mai-chat-tool-call"
    data-tool-status={toolCall.status}
    alignItems="center"
    gap="8px"
    color="neutral.secondary"
    fontSize="13px"
    lineHeight="1.4"
  >
    <StepIcon status={toolCall.status} />
    <ChakraText as="span">{toolCallLabel(toolCall.name)}</ChakraText>
  </ChakraFlex>
);

const StepsSummary = ({ count }: { count: number }) => (
  <ChakraText color="neutral.secondary" fontSize="13px" fontWeight="500">
    {`Steps (${count})`}
  </ChakraText>
);

const MaiChatToolSteps = ({
  toolCalls,
  isThinking,
  onToggle,
}: MaiChatToolStepsProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (toolCalls.length === 0) return null;

  const activeStep = toolCalls.find((t) => t.status === 'pending');

  // Reports the state the click lands in, on every click in either direction. The
  // caller decides what a collapse means; this only says which way it went.
  const handleToggle = () => {
    onToggle(!isExpanded);
    setIsExpanded((prev) => !prev);
  };

  const renderToggleContent = () => {
    if (isExpanded) return <StepsSummary count={toolCalls.length} />;
    if (activeStep) return <StepRow toolCall={activeStep} />;
    if (isThinking) return <MaiChatThinking />;
    return <StepsSummary count={toolCalls.length} />;
  };

  return (
    <ChakraBox data-testid="mai-chat-tool-steps">
      <style>{SPINNER_KEYFRAMES}</style>
      <ChakraFlex
        as="button"
        type="button"
        data-testid="mai-chat-tool-steps__toggle"
        onClick={handleToggle}
        aria-expanded={isExpanded}
        aria-controls="mai-chat-tool-steps__list"
        alignItems="center"
        gap="8px"
        width="100%"
        background="transparent"
        border="0"
        padding="0"
        cursor="pointer"
        textAlign="left"
      >
        {renderToggleContent()}
        <Icon
          name={
            isExpanded
              ? FAIconName.chevronUpRegular
              : FAIconName.chevronDownRegular
          }
          color="neutral.tertiary"
          size="sm"
        />
      </ChakraFlex>

      {isExpanded && (
        <ChakraFlex
          id="mai-chat-tool-steps__list"
          data-testid="mai-chat-tool-steps__list"
          direction="column"
          gap="6px"
          marginTop="6px"
        >
          {toolCalls.map((toolCall) => (
            <StepRow key={toolCall.toolUseId} toolCall={toolCall} />
          ))}
        </ChakraFlex>
      )}
    </ChakraBox>
  );
};

export default MaiChatToolSteps;
